/**
 * =============================================================================
 * ROASTBREW - USER AVATAR
 * =============================================================================
 *
 * The signed-in user's avatar: their initial in the RoastBrew style, cropped to
 * a circle. Used by the sidebar footer and the Profile page.
 *
 * THE ARTWORK lives in avatars.js — one SVG per letter plus the fallback. To
 * change how avatars look, edit that file; this one only decides WHICH avatar.
 *
 * WHICH LETTER (avatarLetter below):
 *   1. The name is a single "Full name" field (accounts.display_name). The
 *      FIRST word gives the initial — normally the first name.
 *   2. If the first word doesn't start with a letter A–Z (e.g. "123" or "李"),
 *      the SECOND word is used instead — normally the last name.
 *   3. Neither works → the RoastBrew fallback. Always; there is no other option
 *      (the email is deliberately not used).
 *   Accents are removed before checking ("Élise" → E, "Øyvind" → O), leading
 *   punctuation is ignored ("(Vin)" → V), and the result is upper-cased
 *   ("vinay" → V).
 *
 * Drawn inline (no image request), so it can never flash as a broken image.
 * =============================================================================
 */

import React from 'react';
import { AVATARS, FALLBACK_AVATAR } from './avatars';

// Letters NFD can't split into base + accent, mapped to the base letter.
// Written as escapes so the file is plain ASCII: æ ø œ ł đ ð þ ß ı
// (capital ẞ is matched too; its lower case is ß)
const LATIN_EXTRAS = {
  '\u00e6': 'a', '\u00f8': 'o', '\u0153': 'o', '\u0142': 'l', '\u0111': 'd',
  '\u00f0': 'd', '\u00fe': 't', '\u00df': 's', '\u0131': 'i',
};

function toPlainLatin(text) {
  return String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')           // combining accents
    .replace(/[\u00e6\u00f8\u0153\u0142\u0111\u00f0\u00fe\u00df\u1e9e\u0131]/gi, (c) => {
      const base = LATIN_EXTRAS[c.toLowerCase()];
      return c === c.toLowerCase() ? base : base.toUpperCase();
    });
}

/** The A–Z initial for this user, or null when the fallback should be used. */
export function avatarLetter(name) {
  const words = toPlainLatin(name).trim().split(/\s+/);
  // First word (first name), then second word (last name). Nothing further.
  for (const word of words.slice(0, 2)) {
    // Ignore leading punctuation, so "(Vin)" or "'Ana" still count.
    const first = word.replace(/^[^\p{L}\p{N}]+/u, '').charAt(0);
    if (/^[A-Za-z]$/.test(first)) return first.toUpperCase();
  }
  return null;
}

/** Add width/height 100% to the stored <svg> so it fills the circle. */
function sized(svg) {
  return svg.replace('<svg ', '<svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice" ');
}

export default function UserAvatar({ name, size = 40, style }) {
  const letter = avatarLetter(name);
  const svg = (letter && AVATARS[letter]) || FALLBACK_AVATAR;

  return (
    <div
      role="img"
      aria-label={letter ? `${name} avatar` : 'RoastBrew avatar'}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        overflow: 'hidden',
        flexShrink: 0,
        lineHeight: 0,
        ...style,
      }}
      // Static artwork from avatars.js only — never user input.
      dangerouslySetInnerHTML={{ __html: sized(svg) }}
    />
  );
}
